import { WarningCircleIcon } from "@phosphor-icons/react";
import {
	type InputHTMLAttributes,
	type ReactNode,
	type TextareaHTMLAttributes,
	forwardRef,
	useId,
} from "react";

import { cn } from "../cn.js";

export interface FieldProps {
	label: string;
	/** Hint shown under the label, before the user makes a mistake. */
	help?: ReactNode;
	error?: string | null;
	required?: boolean;
	/** Rendered at the end of the label row, e.g. a "Generate" action. */
	action?: ReactNode;
	children: (props: { id: string; describedBy: string | undefined; invalid: boolean }) => ReactNode;
	className?: string;
}

/**
 * Form field wrapper.
 *
 * Owns the label/description/error wiring so every control in the product is
 * announced correctly without each screen repeating the aria plumbing.
 */
export function Field({ label, help, error, required, action, children, className }: FieldProps) {
	const id = useId();
	const helpId = `${id}-help`;
	const errorId = `${id}-error`;
	const describedBy = [help ? helpId : null, error ? errorId : null].filter(Boolean).join(" ") || undefined;

	return (
		<div className={cn("flex flex-col gap-1.5", className)}>
			<div className="flex items-baseline justify-between gap-3">
				<label htmlFor={id} className="text-sm font-medium text-(--text-primary)">
					{label}
					{required && (
						<span className="ml-0.5 text-(--color-critical-600)" aria-hidden="true">
							*
						</span>
					)}
				</label>
				{action}
			</div>

			{help && (
				<p id={helpId} className="text-xs text-(--text-muted)">
					{help}
				</p>
			)}

			{children({ id, describedBy, invalid: Boolean(error) })}

			{error && (
				<p id={errorId} role="alert" className="flex items-start gap-1.5 text-xs text-(--color-critical-700)">
					<WarningCircleIcon className="mt-px size-3.5 shrink-0" weight="fill" aria-hidden="true" />
					{error}
				</p>
			)}
		</div>
	);
}

export const controlClassName = cn(
	"w-full rounded-(--radius-md) border bg-(--surface) px-3 text-base md:text-sm text-(--text-primary)",
	"border-(--border-strong) placeholder:text-(--text-muted)",
	"transition-colors focus:border-(--color-brand-500)",
	"disabled:cursor-not-allowed disabled:bg-(--surface-sunken) disabled:text-(--text-muted)",
	"aria-[invalid=true]:border-(--color-critical-500)",
);

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(function Input(
	{ className, ...props },
	ref,
) {
	return <input {...props} ref={ref} className={cn(controlClassName, "h-10", className)} />;
});

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(
	function Textarea({ className, ...props }, ref) {
		return <textarea {...props} ref={ref} className={cn(controlClassName, "min-h-24 py-2", className)} />;
	},
);

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "type"> {
	label: ReactNode;
	description?: ReactNode;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
	{ label, description, className, ...props },
	ref,
) {
	const id = useId();
	return (
		<div className={cn("flex items-start gap-2.5", className)}>
			<input
				{...props}
				ref={ref}
				id={id}
				type="checkbox"
				className="mt-0.5 size-4 shrink-0 flex-none rounded-(--radius-xs) border-(--border-strong) text-(--color-brand-600)"
			/>
			<div className="flex flex-col gap-0.5">
				<label htmlFor={id} className="text-sm text-(--text-primary)">
					{label}
				</label>
				{description && <p className="text-xs text-(--text-muted)">{description}</p>}
			</div>
		</div>
	);
});
